/**
 * useChatPageContext — derives page/entity context from the current route.
 *
 * Returns:
 * - `pageContext`: object to send to the backend so the AI knows what screen is open
 * - `contextLabel`: human-readable label for the placeholder text in ChatInput
 * - `suggestions`: list of contextual suggestion chips for the current screen
 */
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { PageContext } from '@/services/chatApi';

export interface ChatPageContext {
  pageContext: PageContext;
  contextLabel: string;
  suggestions: string[];
}

type ParamRecord = Record<string, string | undefined>;

function buildContext(pathname: string, params: ParamRecord): ChatPageContext {
  // /projects/:id/** — project detail
  if (/^\/projects\/(\d+)/.test(pathname)) {
    const id = Number(params.id ?? pathname.match(/\/projects\/(\d+)/)?.[1]);
    return {
      pageContext: { route: pathname, entity_type: 'project', entity_id: id || undefined },
      contextLabel: 'este projeto',
      suggestions: [
        'Qual é o status atual do projeto?',
        'Quais tarefas estão em atraso?',
        'Quais são os principais riscos?',
        'Como está o progresso do orçamento?',
        'Quais marcos estão próximos?',
      ],
    };
  }

  // /app/intake/** — intake / Bridge 0
  if (pathname.startsWith('/app/intake') || pathname.startsWith('/intake')) {
    const id = params.id ? Number(params.id) : undefined;
    return {
      pageContext: { route: pathname, entity_type: 'intake', entity_id: id },
      contextLabel: 'os intakes',
      suggestions: [
        'Quais intakes estão aguardando triagem?',
        'Qual o status deste intake?',
        'Quais são os critérios de aprovação?',
      ],
    };
  }

  // /committees/** — committees / bridges
  if (pathname.startsWith('/committees') || pathname.startsWith('/app/committees')) {
    return {
      pageContext: { route: pathname, entity_type: 'committee' },
      contextLabel: 'os comitês',
      suggestions: [
        'Quais comitês têm votações pendentes?',
        'Qual é o histórico de decisões recentes?',
        'Quais projetos aguardam aprovação de comitê?',
      ],
    };
  }

  // /bridges/** — specific bridge
  if (pathname.startsWith('/bridges')) {
    return {
      pageContext: { route: pathname, entity_type: 'committee' },
      contextLabel: 'esta bridge',
      suggestions: [
        'Quais projetos estão nesta etapa?',
        'Quais critérios precisam ser atendidos?',
        'Quais itens estão pendentes de aprovação?',
      ],
    };
  }

  // /relatorios — reports
  if (pathname.startsWith('/relatorios') || pathname.startsWith('/reports')) {
    return {
      pageContext: { route: pathname, entity_type: 'reports' },
      contextLabel: 'os relatórios',
      suggestions: [
        'Analise o desempenho geral do portfólio',
        'Quais projetos precisam de atenção urgente?',
        'Como está a taxa de projetos no prazo?',
        'Quais são os riscos mais críticos do portfólio?',
      ],
    };
  }

  // /admin/** — admin area
  if (pathname.startsWith('/admin')) {
    return {
      pageContext: { route: pathname, entity_type: 'admin' },
      contextLabel: 'a área administrativa',
      suggestions: [
        'Quantos usuários aguardam aprovação?',
        'Qual é o uso da plataforma este mês?',
        'Quais alertas estão ativos?',
      ],
    };
  }

  // /home — default dashboard
  return {
    pageContext: { route: pathname, entity_type: 'home' },
    contextLabel: 'o portfólio',
    suggestions: [
      'Quais projetos estão atrasados?',
      'Qual é o resumo do portfólio?',
      'Quais riscos críticos estão ativos?',
      'Como está o progresso geral dos projetos?',
      'Quais marcos vencem esta semana?',
    ],
  };
}

export function useChatPageContext(): ChatPageContext {
  const { pathname } = useLocation();
  const params = useParams<ParamRecord>();

  return useMemo(() => buildContext(pathname, params), [pathname, params]);
}
